import { Heading, Text, Secondary, TextLink } from '@cinch-labs/design-system'
import styles from './site-info-section.module.css'
import { track } from '@cinch-labs/customer-data-segment'
import {
  EventActions,
  EventCategories,
  EventNames,
  sendDigitalDataEvent,
} from '@cinch-nx/shared-analytics'

export interface SiteInfo {
  displayName: string
  coordinates?: {
    latitude: number
    longitude: number
  }
  address?: {
    addressLine1: string
    addressLine2?: string
    city: string
    county?: string
    postcode: string
  }
  openingHours?: {
    monday: { start: string; end: string }
    tuesday: { start: string; end: string }
    wednesday: { start: string; end: string }
    thursday: { start: string; end: string }
    friday: { start: string; end: string }
    saturday: { start: string; end: string }
    sunday: { start: string; end: string }
  }
  organisationalUnit?: string
}

const weekdays = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const

interface SiteInfoSectionProps {
  site: SiteInfo
  contactUs: {
    phoneLabel: string
    emailLabel: string
    telephoneNumber: string
    emailAddress: string
  }
}

export const SiteInfoSection: React.FC<SiteInfoSectionProps> = ({
  site,
  contactUs,
}) => {
  const {
    displayName,
    address,
    openingHours,
    coordinates,
    organisationalUnit,
  } = site
  const containerClassName =
    coordinates === undefined
      ? `${styles.container} ${styles.containerNoCoordinates}`
      : styles.container

  const googleMapsHref = coordinates
    ? `https://www.google.com/maps/dir/?api=1&destination=${coordinates.latitude},${coordinates.longitude}`
    : ''

  const handleOpenGoogleMaps = (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    siteOrganisationalUnit: string,
  ) => {
    event.preventDefault()

    const evt = {
      event: {
        name: EventNames.click,
        category: EventCategories.link,
        action: EventActions.navigate,
        label: 'collection-locations-google-maps-link',
        value: siteOrganisationalUnit,
      },
    }

    sendDigitalDataEvent(evt)
    track('Collection Locations - Google maps link', evt)

    window.open(googleMapsHref, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className={containerClassName}>
      <div className={styles.sectionContainer}>
        <Heading element="h3" className={styles.heading}>
          {displayName}
        </Heading>
        {address === undefined ? (
          <Text fontSizeFixed="s">Address is currently unavailable</Text>
        ) : (
          <>
            <Text fontSizeFixed="s">{address.addressLine1}</Text>
            {address.addressLine2 !== undefined && (
              <Text fontSizeFixed="s">{address.addressLine2}</Text>
            )}
            <Text fontSizeFixed="s">{address.city}</Text>
            {address.county !== undefined && (
              <Text fontSizeFixed="s">{address.county}</Text>
            )}
            <Text fontSizeFixed="s">{address.postcode}</Text>
          </>
        )}
        {site.coordinates && (
          <Secondary
            href={googleMapsHref}
            element="a"
            onClick={(event) =>
              handleOpenGoogleMaps(event, organisationalUnit ?? '')
            }
            classNames={{ button: styles['openMapsButton'] }}
          >
            Open Google map
          </Secondary>
        )}
      </div>
      <div className={styles.sectionContainer}>
        <Heading element="h3" className={styles.heading}>
          Opening Times
        </Heading>
        {openingHours === undefined ? (
          <Text fontSizeFixed="s">Opening times are currently unavailable</Text>
        ) : (
          weekdays.map((day) => {
            const start = openingHours[day].start
            const end = openingHours[day].end
            const display = start === 'closed' ? 'closed' : `${start} - ${end}`

            return (
              <div className={styles.openingHours} key={day}>
                <Text className={styles.dayOfWeek} fontSizeFixed="s">
                  {day}
                </Text>
                <Text fontSizeFixed="s">{display}</Text>
              </div>
            )
          })
        )}
      </div>

      <div className={styles.sectionContainer}>
        <Heading element="h3" className={styles.heading}>
          Need help?
        </Heading>
        <address>
          <Text>
            {contactUs.phoneLabel}{' '}
            <TextLink href={`tel:${contactUs.telephoneNumber}`}>
              {contactUs.telephoneNumber}
            </TextLink>
          </Text>
          <Text>
            {contactUs.emailLabel}{' '}
            <TextLink href={`mailto:${contactUs.emailAddress}`}>
              {contactUs.emailAddress}
            </TextLink>
          </Text>
        </address>
      </div>
    </div>
  )
}
